'use client';

import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { useState, useEffect } from "react";
import { getFingerprint } from "@thumbmarkjs/thumbmarkjs";
import { CheckIcon, Loader2, XIcon } from "lucide-react";

type IsHelpfulProps = {
  contentId: number;
  signature: string;
  isBottom?: boolean;
}

const enum VoteStatus {
  INITIAL = 'initial',
  SUCCESS = 'success',
  SUBMITTING = 'submitting',
  ERROR = 'error',
}

type VoteButtonProps = {
  onVoteClick: (isHelpful: boolean) => Promise<void>;
  isHelpful: boolean;
}
const VoteButton = ({ onVoteClick, isHelpful }: VoteButtonProps) => {
  const [status, setStatus] = useState(VoteStatus.INITIAL);
  const handleVoteClick = () => {
    setStatus(VoteStatus.SUBMITTING);
    onVoteClick(isHelpful)
      .then(() => {
        setStatus(VoteStatus.SUCCESS);
      })
      .catch(() => {
        setStatus(VoteStatus.ERROR);
      });
  }
  return (
    <Button className="flex gap-2" variant="outline" onClick={() => handleVoteClick()} disabled={status === VoteStatus.SUBMITTING}>
      {status === VoteStatus.SUBMITTING && <Loader2 className="animate-spin" />}
      {status === VoteStatus.SUCCESS && <CheckIcon className="text-success" />}
      {status === VoteStatus.ERROR && <XIcon className="text-destructive" />}
      {status === VoteStatus.INITIAL && (isHelpful ? <span>👍</span> : <span>👎</span>)}
      {isHelpful ? <span>Yes</span> : <span>No</span>}
    </Button>
  )
}

const IsHelpful = ({ isBottom = false, contentId, signature }: IsHelpfulProps) => {
  const [, setError] = useState('');
  const [fingerprint, setFingerprint] = useState('');

  useEffect(() => {
    getFingerprint()
      .then((result) => {
        setFingerprint(result);
      })
      .catch((error) => {
        console.error('Error getting fingerprint:', error);
      });
  }, []);

  const handleVote = async (isHelpful: boolean) => {
    try {
      await fetch("/api/vote", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ is_helpful: isHelpful, content_id: contentId, fingerprint, signature }),
      })
        .then(response => response.json())
        .then(data => {
          if (!data.success) {
            throw new Error('Could not submit your vote. Please try again later.');
          }
        })
    } catch (error) {
      setError('Could not submit your vote. Please try again later.');
      throw error;
    }
  }
  return (
    <div className={cn("justify-between items-center", isBottom ? "mt-3 flex lg:hidden" : "hidden lg:flex")}>
      <div className="text-xl font-bold">Was this helpful?</div>
      <div className="flex gap-4">
        <VoteButton onVoteClick={() => handleVote(true)} isHelpful={true} />
        <VoteButton onVoteClick={() => handleVote(false)} isHelpful={false} />
      </div>
    </div>
  )
}


export default IsHelpful;
